import { Box, Link, Stack, Typography } from '@mui/material';
import type { GridInitialState } from '@mui/x-data-grid-premium';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { addMonths, differenceInCalendarMonths, format, parseISO, startOfMonth } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

import {
  MONTH_CELL_CLASS,
  MONTH_CELL_SX,
  MonthCellButton,
} from '~/components/serviceHistory/MonthCellButton';
import type { PeriodWindowRange } from '~/components/serviceHistory/periodWindow';
import { LiftAITable } from '~/components/table';
import type { LiftAIGridColDef } from '~/components/table/colDefTemplate';
import { PeriodCellModal } from '~/components/visitsCompletion/PeriodCellModal';
import {
  achievementLabel,
  outcomeMeta,
  requirementLabel,
} from '~/components/visitsCompletion/visitsCompletionState';
import type {
  MaintenanceVisitCarSummary,
  MaintenanceVisitPeriodCell,
  MaintenanceVisitWindow,
} from '~/data/schemas/maintenanceVisit';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { buildUrlForElevator } from '~/utils/urlBuilder';

const MONTH_COL_WIDTH = 52;

const initialState: GridInitialState = {
  sorting: { sortModel: [{ field: 'missing', sort: 'desc' }] },
};

const periodForMonth = (row: MaintenanceVisitCarSummary, month: string) =>
  row.periods.find((period) => month >= period.periodStart && month <= period.periodEnd);

const periodLabel = (start: string, end: string, months: number) =>
  months === 1
    ? format(parseISO(start), 'MMMM yyyy')
    : `${format(parseISO(start), 'MMM')} to ${format(parseISO(end), 'MMM yyyy')}`;

/**
 * How many month columns this period covers, from this column rightwards.
 *
 * Clamped to the axis so a period reaching outside the chosen window draws clipped
 * rather than dragging the table wider. The period is still scored on all its months;
 * the axis and the scoring are deliberately different spans.
 */
const spanWithin = (period: MaintenanceVisitPeriodCell, months: string[], index: number) => {
  let last = index;
  for (let i = months.length - 1; i >= index; i -= 1) {
    if (months[i] <= period.periodEnd) {
      last = i;
      break;
    }
  }
  return Math.max(1, last - index + 1);
};

export function VisitsCompletionTable({
  summaries,
  loading,
  range,
  window,
  alwaysShowProperty = false,
  onPeriodChanged,
}: {
  summaries: readonly MaintenanceVisitCarSummary[];
  loading: boolean;
  range: PeriodWindowRange;
  window: MaintenanceVisitWindow | null;
  alwaysShowProperty?: boolean;
  onPeriodChanged: () => void;
}) {
  const apiRef = useGridApiRef();
  const tableHeight = useDetailTableHeight();
  const [open, setOpen] = useState<{ carId: string; periodStart: string } | null>(null);

  // Scopes that span buildings need the property to tell two "Car 1" rows apart. A
  // contract is such a scope by nature, so it keeps the column even where today's data
  // happens to sit in one building.
  const qualify = useMemo(
    () => alwaysShowProperty || new Set(summaries.map((row) => row.propertyId)).size > 1,
    [alwaysShowProperty, summaries],
  );

  // The axis is the window the user chose, not the union of every row's period bounds.
  // Taking it from the bounds let a single annual contract stretch the table to 24
  // columns and blank every monthly row beside it.
  const months = useMemo(() => {
    if (!window || summaries.length === 0) return [];
    const first = startOfMonth(parseISO(window.startDate));
    const last = startOfMonth(parseISO(window.endDate));
    const count = differenceInCalendarMonths(last, first) + 1;
    return Array.from({ length: count }, (_, i) => format(addMonths(first, i), 'yyyy-MM-01'));
  }, [window, summaries.length]);

  const columns = useMemo<LiftAIGridColDef<MaintenanceVisitCarSummary>[]>(() => {
    const monthColumns = months.map<LiftAIGridColDef<MaintenanceVisitCarSummary>>(
      (month, index) => ({
        field: `month_${month}`,
        headerName: format(parseISO(month), 'MMM'),
        width: MONTH_COL_WIDTH,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableExport: true,
        cellClassName: MONTH_CELL_CLASS,
        // A period wider than a month is drawn once and spans its remaining columns, so
        // a quarterly row reads as one quarter rather than three identical cells.
        colSpan: (_value, row) => {
          const period = periodForMonth(row, month);
          if (!period) return 1;
          const startsHere = index === 0 || period.periodStart > months[index - 1];
          return startsHere ? spanWithin(period, months, index) : 1;
        },
        valueGetter: (_value, row) => periodForMonth(row, month)?.outcome ?? '',
        renderCell: ({ row }) => {
          const period = periodForMonth(row, month);
          if (!period) return null;
          // Only the column the period starts in draws it; the spanned columns are
          // covered by that cell.
          const startsHere = index === 0 || period.periodStart > months[index - 1];
          if (!startsHere) return null;

          const meta = outcomeMeta(period.outcome);
          const label = periodLabel(period.periodStart, period.periodEnd, row.periodMonths);
          const shortfall = achievementLabel({ ...period, requiredQuantity: row.requiredQuantity });
          const clipped = period.clippedStart || period.clippedEnd;
          return (
            <MonthCellButton
              title={`${row.carName} · ${label} — ${meta.shortLabel}${
                shortfall ? ` (${shortfall})` : ''
              }${clipped ? ' · runs outside the selected period' : ''}${
                period.hoursDerived ? ' · hours derived from start and finish' : ''
              }`}
              label={`${row.carName}, ${label}, ${meta.shortLabel}`}
              onClick={() => setOpen({ carId: row.carId, periodStart: period.periodStart })}
              dimmed={clipped}
              dataAttributes={{ 'data-outcome': period.outcome }}
            >
              <meta.Icon sx={{ fontSize: 18, color: meta.color }} />
              {shortfall && row.periodMonths > 1 ? (
                <Typography variant="caption" color="text.secondary">
                  {shortfall}
                </Typography>
              ) : null}
            </MonthCellButton>
          );
        },
      }),
    );

    return [
      {
        field: 'carName',
        headerName: 'Elevator',
        // Takes whatever the month and count columns leave, so the grid fills its
        // container rather than trailing off into empty space. Safe only because the
        // tab keeps this table unmounted while its panel is hidden: flex resolved
        // against a zero-width container collapses and is never recomputed.
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }) => (
          <Link
            href={buildUrlForElevator(row.carId)}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            underline="hover"
            onClick={(event) => event.stopPropagation()}
          >
            {row.carName}
          </Link>
        ),
      },
      ...(qualify
        ? [
            {
              field: 'propertyName',
              headerName: 'Property',
              flex: 1,
              minWidth: 160,
            } as LiftAIGridColDef<MaintenanceVisitCarSummary>,
          ]
        : []),
      {
        field: 'requirement',
        headerName: 'Requirement',
        width: 150,
        sortable: false,
        // Without this the varying cell widths have no explanation on screen.
        description: 'What the contract asks for, and over how long.',
        valueGetter: (_value, row) => requirementLabel(row),
        renderCell: ({ row }) => (
          <Stack justifyContent="center" height="100%">
            <Typography variant="body2" color="text.secondary">
              {requirementLabel(row)}
            </Typography>
          </Stack>
        ),
      },
      ...monthColumns,
      // One column per outcome a consultant can act on, and no two overlap. A period
      // still running is deliberately not among them: it is visible in its cell and
      // has nothing to chase, so these four fall one short of the period count while
      // the current month is open.
      {
        field: 'done',
        headerName: 'Done',
        type: 'number',
        width: 90,
        align: 'right',
        headerAlign: 'right',
        description: 'Periods the records meet the contract for.',
      },
      {
        field: 'partial',
        headerName: 'Partial',
        type: 'number',
        width: 95,
        align: 'right',
        headerAlign: 'right',
        description: 'Periods some of the work reached us for, but less than the contract asks.',
      },
      {
        field: 'missing',
        headerName: 'No record',
        type: 'number',
        width: 115,
        align: 'right',
        headerAlign: 'right',
        description: 'Periods nothing reached us for, and nobody has accounted for.',
      },
      {
        field: 'skipped',
        headerName: 'Not performed',
        type: 'number',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        description: 'Periods somebody with standing said the visit did not happen in.',
      },
    ];
  }, [months, qualify]);

  const onClose = useCallback(() => setOpen(null), []);

  return (
    <>
      <Box sx={MONTH_CELL_SX}>
        <LiftAITable<MaintenanceVisitCarSummary>
          height={tableHeight}
          apiRef={apiRef}
          rows={summaries as MaintenanceVisitCarSummary[]}
          columns={columns}
          getRowId={(row) => row.carId}
          loading={loading}
          tableHeaderTitle="Maintenance Completion"
          enableQuickFilter
          quickFilterPlaceHolder="Search elevators…"
          initialState={initialState}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          paginationModel={{ pageSize: 25, page: 0 }}
        />
      </Box>
      {open ? (
        <PeriodCellModal
          carId={open.carId}
          periodStart={open.periodStart}
          range={range}
          onClose={onClose}
          onChanged={onPeriodChanged}
        />
      ) : null}
    </>
  );
}
